import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { subYears, endOfMonth, format } from "date-fns";
import { TrendingUp, TrendingDown, Euro, FileText, Target, BarChart3 } from "lucide-react";
import { useDateRange } from "./DateRangeContext";
const AnalyticsSummaryCardsSkeleton = () => <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
    {[1, 2, 3, 4, 5].map(i => <Card key={i}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-24" />
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-4 w-16" />
          </div>
        </CardContent>
      </Card>)}
  </div>;
interface AnalyticsMetrics {
  sales: number;
  salesPrevious: number;
  arr: number;
  arrPrevious: number;
  proposalsCount: number;
  proposalsCountPrevious: number;
  proposalAvgValue: number;
  proposalAvgValuePrevious: number;
  successRate: number;
  successRatePrevious: number;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};
const formatGrowth = (current: number, previous: number) => {
  // Don't show growth if previous is 0 or both values are 0
  if (previous === 0 || current === 0 && previous === 0) {
    return {
      percentage: 0,
      isPositive: true,
      hasData: false
    };
  }
  const growth = (current - previous) / previous * 100;
  return {
    percentage: Math.abs(growth),
    isPositive: growth >= 0,
    hasData: true
  };
};
export function AnalyticsSummaryCards() {
  const {
    dateRange
  } = useDateRange();
  const {
    data: metrics,
    isLoading,
    error
  } = useQuery<AnalyticsMetrics>({
    queryKey: ["analytics-summary", dateRange.from, dateRange.to],
    queryFn: async (): Promise<AnalyticsMetrics> => {
      try {
        console.log('AnalyticsSummaryCards: Starting queries...');
        console.log('AnalyticsSummaryCards: Date range:', {
          from: dateRange.from.toISOString(),
          to: dateRange.to.toISOString()
        });

        // Calculate previous year's same period for comparison (full months)
        // For YTD comparison, we compare current period vs full same months last year
        // Example: YTD Jan 1-8, 2026 compares vs full Jan 2025
        const previousYearFromMonth = dateRange.from.getMonth();
        const previousYearToMonth = dateRange.to.getMonth();
        const previousYearStart = new Date(dateRange.from.getFullYear() - 1, previousYearFromMonth, 1);
        const previousYearEnd = endOfMonth(new Date(dateRange.to.getFullYear() - 1, previousYearToMonth, 1));
        const currentFrom = format(dateRange.from, 'yyyy-MM-dd');
        const currentTo = format(dateRange.to, 'yyyy-MM-dd');
        const previousFrom = format(previousYearStart, 'yyyy-MM-dd');
        const previousTo = format(previousYearEnd, 'yyyy-MM-dd');
        console.log('AnalyticsSummaryCards: Comparison periods:', {
          current: {
            from: currentFrom,
            to: currentTo
          },
          previous: {
            from: previousFrom,
            to: previousTo
          }
        });
        const [proposalsResult, invoicesResult, projectsResult, previousInvoicesResult, previousProposalsResult] = await Promise.all([supabase.from('proposals').select('value, final_value, sent_date, status').not('sent_date', 'is', null).gte('sent_date', currentFrom).lte('sent_date', currentTo).limit(500), supabase.from('invoices').select('amount, issue_date, status').not('issue_date', 'is', null).gte('issue_date', currentFrom).lte('issue_date', currentTo).limit(500),
        // Limit to prevent large data loads
        supabase.from('projects').select('arr, delivered_date, value, sold_for').eq('arr', true),
        // Previous period invoices for comparison
        supabase.from('invoices').select('amount, issue_date, status').not('issue_date', 'is', null).gte('issue_date', previousFrom).lte('issue_date', previousTo).limit(500),
        // Previous period proposals for comparison
        supabase.from('proposals').select('value, final_value, sent_date, status').not('sent_date', 'is', null).gte('sent_date', previousFrom).lte('sent_date', previousTo).limit(500)]);
        console.log('AnalyticsSummaryCards: Query results:', {
          proposalsError: proposalsResult.error,
          proposalsCount: proposalsResult.data?.length,
          invoicesError: invoicesResult.error,
          invoicesCount: invoicesResult.data?.length,
          projectsError: projectsResult.error,
          projectsCount: projectsResult.data?.length,
          previousInvoicesCount: previousInvoicesResult.data?.length,
          previousProposalsCount: previousProposalsResult.data?.length
        });
        if (proposalsResult.error) {
          console.error('AnalyticsSummaryCards: Proposals query error:', proposalsResult.error);
          throw proposalsResult.error;
        }
        if (invoicesResult.error) {
          console.error('AnalyticsSummaryCards: Invoices query error:', invoicesResult.error);
          throw invoicesResult.error;
        }
        if (projectsResult.error) {
          console.error('AnalyticsSummaryCards: Projects query error:', projectsResult.error);
          throw projectsResult.error;
        }
        const proposals = proposalsResult.data || [];
        const invoices = invoicesResult.data || [];
        const projects = projectsResult.data || [];
        const previousInvoices = previousInvoicesResult.data || [];
        const previousProposals = previousProposalsResult.data || [];
        console.log('AnalyticsSummaryCards: Processing data:', {
          proposalsData: proposals.slice(0, 2),
          invoicesData: invoices.slice(0, 2),
          projectsData: projects.slice(0, 2),
          previousInvoicesData: previousInvoices.slice(0, 2),
          previousProposalsData: previousProposals.slice(0, 2)
        });
        console.log('AnalyticsSummaryCards: Processing data:', {
          proposalsData: proposals.slice(0, 2),
          invoicesData: invoices.slice(0, 2),
          projectsData: projects.slice(0, 2)
        });

        // Simple calculations - count all issued invoices (same logic as Sales Projection "Invoiced so far")
        const sales = invoices.filter(inv => inv.issue_date) // Only invoices that have been issued
        .reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);

        // Previous period sales
        const salesPrevious = previousInvoices.filter(inv => inv.issue_date).reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);
        console.log('📊 Analytics Summary Card - Sales calculation:', {
          sales,
          salesPrevious,
          growth: ((sales - salesPrevious) / salesPrevious * 100).toFixed(1),
          invoicesCount: invoices.length,
          previousInvoicesCount: previousInvoices.length,
          dateRange: {
            current: {
              from: currentFrom,
              to: currentTo
            },
            previous: {
              from: previousFrom,
              to: previousTo
            }
          },
          currentPeriodInvoices: invoices.map(i => ({
            amount: i.amount,
            issue_date: i.issue_date
          })).slice(0, 5),
          previousPeriodInvoices: previousInvoices.map(i => ({
            amount: i.amount,
            issue_date: i.issue_date
          })).slice(0, 5)
        });

        // ARR calculation from projects with ARR=true and delivered_date > today
        const today = new Date().toISOString().split('T')[0];
        const arr = projects.filter(project => project.delivered_date && project.delivered_date > today).reduce((sum, project) => {
          const projectValue = Number(project.sold_for) || Number(project.value) || 0;
          return sum + projectValue;
        }, 0);

        // ARR doesn't change much period to period, so we don't show growth
        const arrPrevious = 0;
        console.log('AnalyticsSummaryCards: ARR calculation:', {
          arr,
          projectsCount: projects.length
        });
        const proposalsCount = proposals.filter(p => p.sent_date).length;
        const proposalsCountPrevious = previousProposals.filter(p => p.sent_date).length;

        // Calculate success rate (accepted proposals / total sent proposals)
        const sentProposals = proposals.filter(p => p.sent_date);
        const acceptedCount = sentProposals.filter(p => p.status === 'accepted').length;
        const successRate = sentProposals.length > 0 ? acceptedCount / sentProposals.length * 100 : 0;

        // Previous period success rate
        const previousSentProposals = previousProposals.filter(p => p.sent_date);
        const previousAcceptedCount = previousSentProposals.filter(p => p.status === 'accepted').length;
        const successRatePrevious = previousSentProposals.length > 0 ? previousAcceptedCount / previousSentProposals.length * 100 : 0;
        const totalValue = sentProposals.reduce((sum, p) => sum + (Number(p.final_value || p.value) || 0), 0);
        const proposalAvgValue = sentProposals.length > 0 ? totalValue / sentProposals.length : 0;

        // Previous period average proposal value
        const previousTotalValue = previousSentProposals.reduce((sum, p) => sum + (Number(p.final_value || p.value) || 0), 0);
        const proposalAvgValuePrevious = previousSentProposals.length > 0 ? previousTotalValue / previousSentProposals.length : 0;
        console.log('AnalyticsSummaryCards: Final metrics:', {
          sales,
          arr,
          proposalsCount,
          successRate,
          proposalAvgValue
        });
        return {
          sales,
          salesPrevious,
          arr,
          arrPrevious,
          proposalsCount,
          proposalsCountPrevious,
          proposalAvgValue,
          proposalAvgValuePrevious,
          successRate,
          successRatePrevious
        };
      } catch (err) {
        console.error('AnalyticsSummaryCards: Query error:', err);
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    // 5 minutes
    retry: 2,
    // Only retry twice
    retryDelay: 1000
  });
  if (isLoading) {
    return <AnalyticsSummaryCardsSkeleton />;
  }
  if (error) {
    return <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              Failed to load analytics
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  if (!metrics) return null;
  const salesGrowth = formatGrowth(metrics.sales, metrics.salesPrevious);
  const arrGrowth = formatGrowth(metrics.arr, metrics.arrPrevious);
  const proposalsGrowth = formatGrowth(metrics.proposalsCount, metrics.proposalsCountPrevious);
  const successRateGrowth = formatGrowth(metrics.successRate || 0, metrics.successRatePrevious || 0);
  const avgValueGrowth = formatGrowth(metrics.proposalAvgValue, metrics.proposalAvgValuePrevious);
  return <div className="grid grid-cols-2 md:grid-cols-5 gap-4 animate-fade-in">
      {/* Sales */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Euro className="h-4 w-4 text-primary" />
            Sales
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {formatCurrency(metrics.sales)}
            </div>
            {salesGrowth.hasData && <div className={`flex items-center gap-1 text-sm ${salesGrowth.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {salesGrowth.isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {salesGrowth.percentage.toFixed(1)}% vs YA
              </div>}
          </div>
        </CardContent>
      </Card>

      {/* ARR */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            ARR
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {formatCurrency(metrics.arr)}
            </div>
            {arrGrowth.hasData && <div className={`flex items-center gap-1 text-sm ${arrGrowth.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {arrGrowth.isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {arrGrowth.percentage.toFixed(1)}% vs YA
              </div>}
          </div>
        </CardContent>
      </Card>

      {/* Proposals Count */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Proposals
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {metrics.proposalsCount}
            </div>
            {metrics.proposalsCountPrevious !== undefined && <div className={`flex items-center gap-1 text-sm ${metrics.proposalsCount >= metrics.proposalsCountPrevious ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.proposalsCount >= metrics.proposalsCountPrevious ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {metrics.proposalsCount - metrics.proposalsCountPrevious >= 0 ? '+' : ''}{metrics.proposalsCount - metrics.proposalsCountPrevious} vs YA
              </div>}
          </div>
        </CardContent>
      </Card>

      {/* Success Rate */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            Success Rate
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {(metrics.successRate || 0).toFixed(1)}%
            </div>
            <div className={`flex items-center gap-1 text-sm ${successRateGrowth.isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {successRateGrowth.isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {successRateGrowth.percentage.toFixed(1)}% vs YA
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Proposal Average Value */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">Proposal Value<Target className="h-4 w-4 text-primary" />
            Av. Proposal Value
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {formatCurrency(metrics.proposalAvgValue)}
            </div>
            {metrics.proposalAvgValuePrevious !== undefined && (metrics.proposalAvgValue > 0 || metrics.proposalAvgValuePrevious > 0) && <div className={`flex items-center gap-1 text-sm ${metrics.proposalAvgValue >= metrics.proposalAvgValuePrevious ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.proposalAvgValue >= metrics.proposalAvgValuePrevious ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {metrics.proposalAvgValue - metrics.proposalAvgValuePrevious >= 0 ? '+' : ''}{formatCurrency(metrics.proposalAvgValue - metrics.proposalAvgValuePrevious)} vs YA
              </div>}
          </div>
        </CardContent>
      </Card>

    </div>;
}